import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { declaredApi, type DeclaredValue } from '../api/declared.api.js';
import { useAuth } from '../auth/useAuth.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * What a business client declared it would do, and the authorised way to keep
 * that current once they are live (DEV-92).
 *
 * Everyone who may open the client sees it; administrators and compliance may
 * update it, with a reason. The version the client was approved on stays
 * readable, and every change says who, when, what it was before and why.
 */

const MIN_REASON = 10;

function shown(field: DeclaredValue, value: string): string {
  if (value === '') return 'Not declared';
  if (field.kind === 'choice') return field.options?.find((option) => option.value === value)?.label ?? value;
  if (field.kind === 'amount' || field.kind === 'count') {
    const [whole = '', fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fraction === undefined ? grouped : `${grouped}.${fraction}`;
  }
  return value;
}

/** Free text past this many characters starts folded, so one answer does not bury the rest. */
const FOLD_AT = 480;

/**
 * One declared answer.
 *
 * Numbers and choices sit beside their label. Free text -- a business
 * description can run to a page -- takes the full width under its label:
 * squeezed into the value column of a narrow card it read one word per line.
 */
function DeclaredRow({ field, dense = false }: { readonly field: DeclaredValue; readonly dense?: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const value = shown(field, field.value);
  const variant = dense ? 'caption' : 'body2';
  const rule = dense ? {} : { borderBottom: '1px solid', borderColor: 'divider' };

  if (field.kind === 'text' && field.value !== '') {
    const long = field.value.length > FOLD_AT;
    return (
      <Box sx={{ py: dense ? 0.5 : 1, ...rule }}>
        <Typography component="dt" variant={variant} color="text.secondary" sx={{ mb: 0.75 }}>{field.label}</Typography>
        <Box component="dd" sx={{ m: 0, px: 1.5, py: 1.25, borderRadius: 1, bgcolor: 'action.hover', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
          <Typography
            variant={variant}
            sx={{
              whiteSpace: 'pre-wrap', overflowWrap: 'break-word', lineHeight: 1.65,
              ...(long && !open ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
            }}
          >
            {value}
          </Typography>
          {long ? (
            <Button size="small" sx={{ mt: 0.5, ml: -0.75, textTransform: 'none' }} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? 'Show less' : 'Show all'}
            </Button>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.1fr) minmax(0, 1fr)' }, columnGap: 3, rowGap: 0.25, py: dense ? 0.5 : 0.75, ...rule }}>
      <Typography component="dt" variant={variant} color="text.secondary">{field.label}</Typography>
      <Typography component="dd" variant={variant} sx={{ m: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

export function DeclaredInformationPanel({ customerId }: { readonly customerId: string }): JSX.Element | null {
  const { user } = useAuth();
  const mayUpdate = user?.role === 'admin' || user?.role === 'compliance';
  const queryClient = useQueryClient();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['declared-information', customerId],
    queryFn: () => declaredApi.get(customerId),
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showDecided, setShowDecided] = useState(false);

  if (isLoading) return <Paper sx={{ p: 3 }}><CircularProgress size={20} aria-label="Loading declared information" /></Paper>;
  // No onboarding record, or a personal client: nothing declared to show.
  if (error !== null || data === undefined || !Array.isArray(data.fields) || !Array.isArray(data.history)) return null;
  if (data.accountType !== 'BUSINESS') return null;

  const changes = data.fields
    .filter((field) => draft[field.path] !== undefined && draft[field.path]?.trim() !== field.value)
    .map((field) => ({ path: field.path, value: draft[field.path]?.trim() ?? '' }));

  const start = (): void => {
    setDraft(Object.fromEntries(data.fields.map((field) => [field.path, field.value])));
    setReason('');
    setSaveError('');
    setOpen(true);
  };

  const save = (): void => {
    setSaving(true);
    setSaveError('');
    declaredApi.update(data.applicationId, changes, reason.trim())
      .then(async () => {
        setOpen(false);
        // The application page shows the same snapshot and its edit history.
        await Promise.all([
          refetch(),
          queryClient.invalidateQueries({ queryKey: ['onboarding-application', data.applicationId] }),
          queryClient.invalidateQueries({ queryKey: ['application-edits', data.applicationId] }),
        ]);
      })
      .catch((err: unknown) => setSaveError(err instanceof Error ? err.message : 'The declaration could not be updated.'))
      .finally(() => setSaving(false));
  };

  return (
    <Paper component="section" sx={{ p: { xs: 2, sm: 3 } }} aria-labelledby="declared-title">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
        <Typography variant="h6" component="h3" id="declared-title">Declared activity</Typography>
        {mayUpdate && data.updatable ? (
          <Button size="small" variant="outlined" onClick={start}>Update declared information</Button>
        ) : null}
      </Stack>
      {!data.updatable ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Updated here once the client is approved. Until then the application itself is edited.
        </Typography>
      ) : null}

      <Box component="dl" sx={{ m: 0, mt: 1.5 }}>
        {data.fields.map((field) => <DeclaredRow key={field.path} field={field} />)}
      </Box>

      {data.asDecided !== null ? (
        <Box sx={{ mt: 2 }}>
          <Button size="small" onClick={() => setShowDecided((v) => !v)} aria-expanded={showDecided}>
            {showDecided ? 'Hide the version approved at onboarding' : 'Show the version approved at onboarding'}
          </Button>
          {showDecided ? (
            <Box component="dl" sx={{ m: 0, mt: 1 }} aria-label="As approved at onboarding">
              {data.asDecided.map((field) => <DeclaredRow key={field.path} field={field} dense />)}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {data.history.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Changes</Typography>
          <Stack component="ol" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Changes to the declaration">
            {data.history.map((entry) => {
              const field = data.fields.find((f) => f.path === entry.path);
              return (
                <Box component="li" key={entry.id} sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.5 }}>
                  <Typography variant="body2">
                    <b>{entry.label}</b>: {field === undefined ? entry.before || 'Not declared' : shown(field, entry.before)} → {field === undefined ? entry.after : shown(field, entry.after)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(entry.at).toLocaleString(APP_LOCALE)} · {entry.byName ?? entry.by}
                    {entry.kind === 'DECLARED_UPDATE' ? ' · after approval' : ' · during review'}
                  </Typography>
                  <Typography variant="caption" display="block">{entry.reason}</Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="declared-update-title">
        <DialogTitle id="declared-update-title">Update declared information</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The version the client was approved on is kept, and each change is recorded with your
              name and reason.
            </Typography>
            {saveError !== '' ? <Alert severity="error">{saveError}</Alert> : null}
            {data.fields.map((field) => field.kind === 'choice' ? (
              <TextField key={field.path} select label={field.label} value={draft[field.path] ?? ''}
                onChange={(event) => setDraft((prev) => ({ ...prev, [field.path]: event.target.value }))}>
                {(field.options ?? []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            ) : (
              <TextField key={field.path} label={field.label} value={draft[field.path] ?? ''}
                multiline={field.kind === 'text'} minRows={field.kind === 'text' ? 2 : 1}
                inputProps={{ inputMode: field.kind === 'text' ? 'text' : 'numeric', maxLength: 2000 }}
                onChange={(event) => setDraft((prev) => ({ ...prev, [field.path]: event.target.value }))} />
            ))}
            <TextField label="Why is it changing?" required multiline minRows={2} value={reason}
              onChange={(event) => setReason(event.target.value)}
              helperText={`At least ${String(MIN_REASON)} characters. Kept with the change.`}
              inputProps={{ maxLength: 1024 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || changes.length === 0 || reason.trim().length < MIN_REASON}>
            {saving ? 'Saving…' : changes.length === 0 ? 'Nothing changed yet' : `Save ${String(changes.length)} change${changes.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
